import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { FilterService, ExportJobRequestDTO } from '../../shared/loadreportwithfilters/filterservice.service';

export interface ReportDashboardRow {
  jobId: number;
  reportType: string;
  fileName: string;
  generatedDate: string;
  filtersRaw: string;
  status: string;
  expiryDate: string;
  errorInfo: string;
}

export interface FilterCard {
  label: string;
  value: string;
  isFiltered: boolean;
}

@Component({
  selector: 'app-reports-dashboard',
  templateUrl: './reports-dashboard.component.html',
  styleUrl: './reports-dashboard.component.scss',
  standalone: false
})
export class ReportsDashboardComponent implements OnInit {

  reportsDisplayedColumns: string[] = ['reportType', 'fileName', 'generatedDate', 'filters', 'status', 'expiryDate', 'download'];
  failedFilesDisplayedColumns: string[] = ['reportType', 'fileName', 'generatedDate', 'filters', 'status', 'errorInfo'];

  reportsDataSource = new MatTableDataSource<ReportDashboardRow>([]);
  failedFilesDataSource = new MatTableDataSource<ReportDashboardRow>([]);

  isLoading = false;
  loadError = false;

  showFiltersPopup = false;
  filterCards: FilterCard[] = [];

  showErrorInfoPopup = false;
  selectedErrorInfo = '';

  /* ── Column filter state ── */
  columnFilters: Record<string, string> = {};
  activeFilterColumn: string | null = null;
  filterInputX = 0;
  filterInputY = 0;

  @ViewChild('reportsSort', { read: MatSort }) set reportsSort(s: MatSort) {
    if (s) this.reportsDataSource.sort = s;
  }
  @ViewChild('failedSort', { read: MatSort }) set failedSort(s: MatSort) {
    if (s) this.failedFilesDataSource.sort = s;
  }
  @ViewChild(MatPaginator) set paginator(p: MatPaginator) {
    if (p) this.reportsDataSource.paginator = p;
  }
  @ViewChild('failedPaginator') set failedPaginator(p: MatPaginator) {
    if (p) this.failedFilesDataSource.paginator = p;
  }

  constructor(
    private location: Location,
    private filterService: FilterService
  ) {}

  ngOnInit(): void {
    this.reportsDataSource.filterPredicate = this.columnFilterPredicate();
    this.failedFilesDataSource.filterPredicate = this.columnFilterPredicate();
    this.loadReports();
  }


  private loadReports(): void {
    this.isLoading = true;
    this.loadError = false;

    this.filterService.GetExportJobRequests().subscribe({
      next: (data: ExportJobRequestDTO[]) => {
        const rows = data.map(item => this.mapToRow(item)).sort((a, b) => this.compareGeneratedDateDesc(a, b));
        this.reportsDataSource.data = rows.filter(r => r.status !== 'Failed');
        this.failedFilesDataSource.data = rows.filter(r => r.status === 'Failed');
        this.isLoading = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
      }
    });
  }

  private columnFilterPredicate() {
    return (row: ReportDashboardRow, filterJson: string): boolean => {
      if (!filterJson) return true;
      const filters = JSON.parse(filterJson) as Record<string, string>;
      return Object.entries(filters).every(([col, val]) => {
        if (!val) return true;
        const cell = String((row as any)[col] ?? '').toLowerCase();
        return cell.includes(val.toLowerCase());
      });
    };
  }

  private applyFilters(source: MatTableDataSource<ReportDashboardRow>): void {
    source.filter = JSON.stringify(this.columnFilters);
    if (source.paginator) source.paginator.firstPage();
  }

  openFilter(column: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.activeFilterColumn === column) {
      this.activeFilterColumn = null;
      return;
    }
    const btn = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.filterInputX = btn.left;
    this.filterInputY = btn.bottom + 4;
    this.activeFilterColumn = column;
  }

  onFilterInput(column: string, value: string): void {
    if (value) {
      this.columnFilters[column] = value;
    } else {
      delete this.columnFilters[column];
    }
    this.applyFilters(this.reportsDataSource);
    this.applyFilters(this.failedFilesDataSource);
  }

  clearColumnFilter(column: string): void {
    delete this.columnFilters[column];
    this.activeFilterColumn = null;
    this.applyFilters(this.reportsDataSource);
    this.applyFilters(this.failedFilesDataSource);
  }

  closeActiveFilter(): void {
    this.activeFilterColumn = null;
  }

  hasActiveFilter(column: string): boolean {
    return !!this.columnFilters[column];
  }

  private compareGeneratedDateDesc(a: ReportDashboardRow, b: ReportDashboardRow): number {
    const aTime = new Date(a.generatedDate.replace(/(AM|PM)/i, ' $1')).getTime();
    const bTime = new Date(b.generatedDate.replace(/(AM|PM)/i, ' $1')).getTime();
    return (isNaN(bTime) ? 0 : bTime) - (isNaN(aTime) ? 0 : aTime);
  }

  private mapToRow(dto: ExportJobRequestDTO): ReportDashboardRow {
    return {
      jobId: dto.jobId,
      reportType: dto.reportType ?? '',
      fileName: dto.fileName ?? '',
      generatedDate: dto.generatedDate ?? '',
      filtersRaw: dto.filters ?? '',
      status: dto.status ?? '',
      expiryDate: dto.expiryDate ?? '',
      errorInfo: dto.errorInfo ?? '',
    };
  }

  viewFilters(row: ReportDashboardRow): void {
    this.filterCards = this.buildFilterCards(row.filtersRaw);
    this.showFiltersPopup = true;
  }

  private buildFilterCards(raw: string): FilterCard[] {
    let c: any = {};
    if (raw) {
      try { c = JSON.parse(raw); } catch { /* treat as no filters */ }
    }

    const nameList = (arr: { displayName: string }[] | undefined): string =>
      arr && arr.length ? arr.map(x => x.displayName).join(', ') : 'All';

    const numList = (arr: number[] | undefined): string =>
      arr && arr.length ? arr.join(', ') : 'All';

    const card = (label: string, value: string): FilterCard =>
      ({ label, value, isFiltered: value !== 'All' });

    return [
      card('Compare Group', c.CompareGroup?.[0] || 'All NRP Participants'),
      card('System', nameList(c.SystemList)),
      card('Organization', nameList(c.OrgNamesList)),
      card('Year', numList(c.YearList)),
      card('Cohort', nameList(c.CohortDatesList)),
      card('Age', c.AgeList ? nameList(c.AgeList) : 'All'),
      card('Nursing Degree Received', c.DegreeAwardedList ? nameList(c.DegreeAwardedList) : 'All'),
      card('Ethnicity', c.EthnicityList ? nameList(c.EthnicityList) : 'All'),
      card('Gender', c.GenderList ? nameList(c.GenderList) : 'All'),
      card('GPA', c.GPAList ? nameList(c.GPAList) : 'All'),
      card('Previous Degree', c.PreviousDegreeList ? nameList(c.PreviousDegreeList) : 'All'),
      card('Unit', c.UnitList ? nameList(c.UnitList) : 'All'),
      card('Termination Reason', c.TerminationList ? nameList(c.TerminationList) : 'All'),
      card('Employment Status', c.EmploymentStatusList ? nameList(c.EmploymentStatusList) : 'All'),
      card('Previous Health Care Experience', c.PreviousHealthCareExperienceList ? nameList(c.PreviousHealthCareExperienceList) : 'All'),
    ];
  }

  closeFiltersPopup(): void {
    this.showFiltersPopup = false;
    this.filterCards = [];
  }

  viewErrorInfo(row: ReportDashboardRow): void {
    this.selectedErrorInfo = row.errorInfo || 'No error information available.';
    this.showErrorInfoPopup = true;
  }

  closeErrorInfoPopup(): void {
    this.showErrorInfoPopup = false;
    this.selectedErrorInfo = '';
  }

  goBack(): void {
    this.location.back();
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      'Completed': 'status-completed',
      'In Progress': 'status-in-progress',
      'In progress': 'status-in-progress',
      'Pending': 'status-pending',
      'Resolved': 'status-resolved',
      'Cancelled': 'status-cancelled',
      'Failed': 'status-error',
      'Under review': 'status-under-review',
      'Scheduled': 'status-scheduled',
    };
    return map[status] ?? 'status-default';
  }

  isDownloadable(status: string, expiryDate: string): boolean {
    if (status !== 'Completed') return false;
    if (!expiryDate) return true;
    const normalized = expiryDate.replace(/(AM|PM)/i, ' $1');
    const expiry = new Date(normalized);
    if (isNaN(expiry.getTime())) return true;
    return expiry >= new Date();
  }

  downloadTooltip(status: string, expiryDate: string): string {
    if (status === 'Cancelled') return 'Export is cancelled';
    if (status === 'Pending') return 'Export is pending';
    if (status !== 'Completed') return `Not available — status is ${status}`;
    if (expiryDate) {
      const normalized = expiryDate.replace(/(AM|PM)/i, ' $1');
      const expiry = new Date(normalized);
      if (!isNaN(expiry.getTime()) && expiry < new Date()) return 'Download expired';
    }
    return 'Download';
  }

  downloadReport(row: ReportDashboardRow): void {
    this.filterService.DownloadExportFile(row.jobId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = row.fileName;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        console.error('Failed to download file:', row.fileName);
      }
    });
  }
}
